import {
  Box,
  Button,
  Checkbox,
  HStack,
  IconButton,
  NativeSelect,
  Popover,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react'
import { LuListFilter, LuX } from 'react-icons/lu'
import type { OrderStatus } from '@/common/order.common'

// The six review states a union order can be in, in the order shown in the
// design. Labels map onto the API statuses via mapApiStatusToUiStatus.
const STATUS_OPTIONS: { value: OrderStatus; label: string }[] = [
  { value: 'SUBMITTED', label: 'Offen' },
  { value: 'APPROVED', label: 'Freigegeben' },
  { value: 'REJECTED', label: 'Abgelehnt' },
  { value: 'CORRECTED', label: 'Korrektur' },
  { value: 'COMPLETED', label: 'Korrektur akzeptiert' },
  { value: 'DELETED', label: 'Korrektur abgelehnt' },
]

// Same month list as the Reporting page (UnionExportPage), value = 1-12.
const MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
]

type ClearableSelectProps = {
  label: string
  allLabel: string
  options: { value: number; label: string }[]
  value: number | null
  onChange: (value: number | null) => void
}

// A numeric select whose empty option means "all", with an inline reset button
// once a value is chosen. Shared by the order-year and order-date filters.
function ClearableSelect({ label, allLabel, options, value, onChange }: ClearableSelectProps) {
  return (
    <Box position="relative">
      <NativeSelect.Root size="sm">
        <NativeSelect.Field
          pr="14"
          aria-label={label}
          value={value ?? ''}
          onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">{allLabel}</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
      {value != null && (
        <IconButton
          aria-label={`${label} zurücksetzen`}
          size="xs"
          variant="ghost"
          position="absolute"
          top="50%"
          right="8"
          transform="translateY(-50%)"
          onClick={() => onChange(null)}
        >
          <LuX />
        </IconButton>
      )}
    </Box>
  )
}

type Props = {
  orderYear: number | null
  orderYearOptions: number[]
  onOrderYearChange: (orderYear: number | null) => void
  year: number | null
  availableYears: number[]
  onYearChange: (year: number | null) => void
  month: number | null
  onMonthChange: (month: number | null) => void
  activeStatuses: OrderStatus[]
  onStatusesChange: (statuses: OrderStatus[]) => void
}

export function UnionOrderOverviewFilterPopover({
  orderYear,
  orderYearOptions,
  onOrderYearChange,
  year,
  availableYears,
  onYearChange,
  month,
  onMonthChange,
  activeStatuses,
  onStatusesChange,
}: Props) {
  function toggle(status: OrderStatus) {
    onStatusesChange(
      activeStatuses.includes(status)
        ? activeStatuses.filter(s => s !== status)
        : [...activeStatuses, status]
    )
  }

  const hasActiveFilter =
    orderYear != null || year != null || month != null || activeStatuses.length > 0

  return (
    <Popover.Root positioning={{ placement: 'bottom-end', gutter: 8 }}>
      <Popover.Trigger asChild>
        <Button
          variant={hasActiveFilter ? 'solid' : 'subtle'}
          colorPalette={hasActiveFilter ? 'teal' : 'gray'}
          size="sm"
        >
          <HStack gap={2}>
            <LuListFilter />
            <Text>Filter</Text>
          </HStack>
        </Button>
      </Popover.Trigger>

      <Portal>
        <Popover.Positioner>
          <Popover.Content borderRadius="2xl" p="4" minW="260px">
            <Popover.Body p="0">
              <VStack align="stretch" gap="4">
                {/* Order year (SIB-1073): the planning/service year the order is
                    for, chosen by the SIB when ordering - not the date it was
                    placed, which the Bestelldatum filter below covers. */}
                <VStack align="stretch" gap="2">
                  <Text fontWeight="700">Bestelljahr</Text>
                  <Text textStyle="metadata" color="fg.muted">
                    Jahr, für das bestellt wurde
                  </Text>
                  <ClearableSelect
                    label="Bestelljahr"
                    allLabel="Alle Bestelljahre"
                    options={orderYearOptions.map(y => ({ value: y, label: String(y) }))}
                    value={orderYear}
                    onChange={onOrderYearChange}
                  />
                </VStack>

                {/* Month/year pair like the Reporting page (SIB-866/SIB-911).
                    Filters on the order's submission date (createdAt), not the
                    Zeitraum column, which shows the ordered service period. */}
                <VStack align="stretch" gap="2">
                  <Text fontWeight="700">Bestelldatum</Text>
                  <ClearableSelect
                    label="Monat"
                    allLabel="Alle Monate"
                    options={MONTH_NAMES.map((name, idx) => ({ value: idx + 1, label: name }))}
                    value={month}
                    onChange={onMonthChange}
                  />
                  <ClearableSelect
                    label="Jahr"
                    allLabel="Alle Jahre"
                    options={availableYears.map(y => ({ value: y, label: String(y) }))}
                    value={year}
                    onChange={onYearChange}
                  />
                </VStack>

                <VStack align="stretch" gap="3">
                  <Text fontWeight="700">Status</Text>
                  {STATUS_OPTIONS.map(opt => (
                    <Checkbox.Root
                      key={opt.value}
                      checked={activeStatuses.includes(opt.value)}
                      onCheckedChange={() => toggle(opt.value)}
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                      <Checkbox.Label>{opt.label}</Checkbox.Label>
                    </Checkbox.Root>
                  ))}
                </VStack>
              </VStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}
